import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Drawer, Form, InputNumber, Input, Popconfirm, Select,
  Space, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { WarningOutlined, GiftOutlined, SyncOutlined } from '@ant-design/icons';
import AppLayout from '../../components/layout/AppLayout';
import {
  useB2BApi, type TOrganisationOption, type TEcoleB2B, type TEcoleB2BUpsert,
  type TAbonnementOrg, type TAbonnementUpsert, type TSignalement,
} from '../../api/paiement/b2b';
import './Pricing.css';

const CURRENCY_OPTIONS = [
  { label: 'MAD', value: 'MAD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' },
];

// Les deux offres sont connues d'avance : on les PRESENTE toutes les deux au
// lieu de faire « ajouter un abonnement » dans un menu de deux entrees.
const OFFRES = [
  {
    code: 'SIEGE_ECOLE',
    label: 'Sièges élèves',
    // Le prix vient du contrat : le ressaisir ici ouvrirait la porte a facturer
    // une ecole a un tarif qu'elle n'a jamais signe.
    prixDuContrat: true,
    aide: "Les élèves réellement passés premium, au tarif négocié avec l'établissement.",
  },
  {
    code: 'TILMISCHOOL',
    label: 'TilmiSchool',
    prixDuContrat: false,
    // Pre-rempli, mais modifiable : le tarif courant est 3 DH, une ecole peut
    // en negocier un autre.
    prixParDefaut: 3,
    aide: "Tous les élèves inscrits au dossier. Gratuit dès que le seuil de sièges premium est atteint.",
  },
] as const;

type EcoleFormValues = {
  organization_id: number;
  code: string;
  prix_siege: number;
  prix_tilmischool?: number;
  devise: string;
  mode_paiement: string;
  periode?: [Dayjs, Dayjs];
  seuil_gratuite?: number;
  actif?: boolean;
};

// Un formulaire par offre : seuls le prestataire et, pour TilmiSchool, le prix
// restent a choisir. Les identifiants Lemon Squeezy n'y figurent pas — le
// serveur les retrouve au premier recomptage, les demander serait leur faire
// porter une panne qui ne les concerne pas.
type AbonnementFormValues = {
  vendor: string;
  prix_unitaire?: number;
};

const money = (v: number, devise: string) =>
  `${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${devise || ''}`;

const PricingB2B: React.FC = () => {
  const { t } = useTranslation();
  const api = useB2BApi();
  const [form] = Form.useForm<EcoleFormValues>();

  const [ecoles, setEcoles] = useState<TEcoleB2B[]>([]);
  // Établissements proposables dans le menu déroulant du formulaire.
  const [organisations, setOrganisations] = useState<TOrganisationOption[]>([]);
  const [loading, setLoading] = useState(false);
  // Établissement dont on configure les abonnements. Null = tiroir fermé.
  const [selected, setSelected] = useState<TEcoleB2B | null>(null);
  const [abonnements, setAbonnements] = useState<TAbonnementOrg[]>([]);
  const [signalements, setSignalements] = useState<TSignalement[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Les deux listes en parallèle : le formulaire est inutilisable sans les
      // organisations, et le tableau sans les contrats.
      const [contrats, orgs] = await Promise.all([api.listEcoles(), api.listOrganisations()]);
      setEcoles(contrats);
      setOrganisations(orgs);
    } catch {
      message.error(t('b2b.loadError', 'Échec du chargement des écoles'));
    } finally {
      setLoading(false);
    }
    // api change d'identité à chaque rendu : le laisser en dépendance
    // relancerait le chargement en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const onFinish = async (values: EcoleFormValues) => {
    setLoading(true);
    try {
      const payload: TEcoleB2BUpsert = {
        organization_id: values.organization_id,
        mode_paiement: values.mode_paiement || 'parent',
        code: values.code,
        prix_siege: values.prix_siege,
        devise: values.devise,
        actif: values.actif ?? true,
      };
      if (values.periode?.length === 2) {
        payload.date_debut = values.periode[0].startOf('day').toISOString();
        payload.date_fin = values.periode[1].endOf('day').toISOString();
      }
      // Champ laissé vide = pas de gratuité négociée : on ne l'envoie pas,
      // le serveur conserve alors ce qu'il a.
      if (values.seuil_gratuite != null) {
        payload.seuil_gratuite = values.seuil_gratuite;
      }
      // Prix TilmiSchool : laissé vide = cette école n'a pas le produit.
      // Distinct de 0, qui vaut « gratuit » et reste facturé à montant nul.
      if (values.prix_tilmischool != null) {
        payload.prix_tilmischool = values.prix_tilmischool;
      }

      await api.upsertEcole(payload);
      message.success(t('b2b.saved', 'Contrat école enregistré'));
      form.resetFields();
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.error || t('b2b.saveError', "Échec de l'enregistrement"));
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (row: TEcoleB2B) => {
    form.setFieldsValue({
      organization_id: row.organization_id,
      mode_paiement: row.mode_paiement || 'parent',
      code: row.code,
      prix_siege: row.prix_siege,
      prix_tilmischool: row.prix_tilmischool,
      devise: row.devise,
      seuil_gratuite: row.seuil_gratuite,
      actif: row.actif,
      periode:
        row.date_debut && row.date_fin
          ? [dayjs(row.date_debut), dayjs(row.date_fin)]
          : undefined,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (row: TEcoleB2B) => {
    setLoading(true);
    try {
      await api.deleteEcole(row.promo_code_id);
      message.success(t('b2b.deleted', 'Contrat supprimé'));
      await load();
    } catch (err: any) {
      // 409 : des sièges s'y rattachent. Le message du serveur explique la
      // marche à suivre (désactiver), il est plus utile que le nôtre.
      message.error(err?.response?.data?.error || t('b2b.deleteError', 'Échec de la suppression'));
    } finally {
      setLoading(false);
    }
  };

  const openAbonnements = async (row: TEcoleB2B) => {
    setSelected(row);
    try {
      const [abos, sigs] = await Promise.all([
        api.listAbonnements(row.organization_id),
        api.listSignalements(row.organization_id),
      ]);
      setAbonnements(abos);
      setSignalements(sigs);
    } catch {
      message.error(t('b2b.aboLoadError', 'Échec du chargement des abonnements'));
    }
  };

  // Décision de l'administration sur un signalement. Retirer exclut le siège
  // du renouvellement : l'élève garde son accès jusqu'à la fin de la période
  // payée, et le mois entamé reste dû à l'école.
  const onTraiterSignalement = async (sig: TSignalement, retirer: boolean) => {
    if (!selected) return;
    try {
      await api.traiterSignalement(sig.id, retirer);
      message.success(
        retirer
          ? t('b2b.sigRemoved', 'Siège retiré — accès maintenu jusqu\'à la fin du mois payé')
          : t('b2b.sigKept', 'Siège confirmé')
      );
      setSignalements(await api.listSignalements(selected.organization_id));
      await load();
    } catch {
      message.error(t('b2b.sigError', 'Échec du traitement'));
    }
  };

  const onSaveAbonnement = async (offreCode: string, values: AbonnementFormValues) => {
    if (!selected) return;
    try {
      // Le prix des sièges vient du CONTRAT, jamais d'une saisie : deux valeurs
      // pour le même tarif finiraient par diverger, et on facturerait l'école à
      // un prix qu'elle n'a pas signé.
      const prix =
        offreCode === 'SIEGE_ECOLE' ? selected.prix_siege : Number(values.prix_unitaire ?? 0);
      const payload: TAbonnementUpsert = {
        organization_id: selected.organization_id,
        offre_code: offreCode,
        vendor: values.vendor,
        prix_unitaire: prix,
        // La devise suit le contrat, pour la même raison.
        devise: selected.devise,
      };
      await api.upsertAbonnement(payload);
      message.success(t('b2b.aboSaved', 'Abonnement enregistré'));
      setAbonnements(await api.listAbonnements(selected.organization_id));
    } catch (err: any) {
      message.error(err?.response?.data?.error || t('b2b.aboSaveError', "Échec de l'enregistrement"));
    }
  };

  const onSync = async () => {
    setLoading(true);
    try {
      const { data } = await api.sync();
      message.success(
        t('b2b.synced', '{{ajustes}} abonnement(s) ajusté(s), {{echecs}} échec(s)', {
          ajustes: data?.ajustes ?? 0,
          echecs: data?.echecs ?? 0,
        })
      );
      await load();
    } catch {
      message.error(t('b2b.syncError', 'Échec du recomptage'));
    } finally {
      setLoading(false);
    }
  };

  const alertes = useMemo(() => ecoles.filter((e) => e.depassement), [ecoles]);

  const columns: ColumnsType<TEcoleB2B> = useMemo(
    () => [
      {
        title: t('b2b.school', 'École'),
        key: 'ecole',
        render: (_, r) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{r.organization_login || `#${r.organization_id}`}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('b2b.code', 'Code')} : <code>{r.code}</code>
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: t('b2b.seatPrice', 'Prix Tilmicode / élève / mois'),
        key: 'prix_siege',
        width: 130,
        align: 'right',
        render: (_, r) => money(r.prix_siege, r.devise),
      },
      {
        title: t('b2b.seats', 'Sièges / Effectif'),
        key: 'sieges',
        width: 160,
        align: 'center',
        render: (_, r) => (
          <Space size={4}>
            <Typography.Text strong>{r.sieges_actifs}</Typography.Text>
            <Typography.Text type="secondary">/ {r.effectif_ecole || '—'}</Typography.Text>
            {r.depassement && (
              <Tooltip
                title={t(
                  'b2b.overflowHint',
                  "Plus de sièges consommés que d'élèves inscrits. Le code circule peut-être hors de l'école, ou l'effectif n'est pas encore chargé."
                )}
              >
                <WarningOutlined style={{ color: '#faad14' }} />
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: t('b2b.amountDue', 'Montant dû'),
        key: 'montant',
        width: 140,
        align: 'right',
        render: (_, r) => <strong>{money(r.montant_sieges, r.devise)}</strong>,
      },
      {
        title: t('b2b.threshold', 'Seuil gratuité'),
        key: 'seuil',
        width: 160,
        align: 'center',
        render: (_, r) =>
          r.seuil_gratuite ? (
            r.tilmischool_gratuit ? (
              <Tag icon={<GiftOutlined />} color="green">
                {t('b2b.tilmischoolFree', 'TilmiSchool offert')}
              </Tag>
            ) : (
              <Typography.Text type="secondary">
                {r.sieges_actifs} / {r.seuil_gratuite}
              </Typography.Text>
            )
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          ),
      },
      {
        title: t('b2b.status', 'Statut'),
        key: 'actif',
        width: 100,
        render: (_, r) =>
          r.actif ? (
            <Tag color="blue">{t('b2b.active', 'Actif')}</Tag>
          ) : (
            <Tag>{t('b2b.inactive', 'Inactif')}</Tag>
          ),
      },
      {
        title: t('b2b.actions', 'Actions'),
        key: 'actions',
        width: 240,
        render: (_, r) => (
          <Space size={2}>
            <Button type="link" size="small" onClick={() => onEdit(r)}>
              {t('b2b.edit', 'Modifier')}
            </Button>
            <Button type="link" size="small" onClick={() => openAbonnements(r)}>
              {t('b2b.subscriptions', 'Abonnements')}
            </Button>
            <Popconfirm
              title={t('b2b.deleteTitle', 'Supprimer ce contrat ?')}
              description={t(
                'b2b.deleteWarning',
                "Impossible si des sièges y sont rattachés : désactivez-le plutôt, l'historique de facturation reste intact."
              )}
              okText={t('b2b.deleteConfirm', 'Supprimer')}
              cancelText={t('b2b.cancel', 'Annuler')}
              okButtonProps={{ danger: true }}
              onConfirm={() => onDelete(r)}
            >
              <Button type="link" size="small" danger>
                {t('b2b.delete', 'Supprimer')}
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // onEdit/onDelete/openAbonnements se recréent à chaque rendu : les mettre
    // en dépendance reconstruirait les colonnes sans fin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  return (
    <AppLayout title={t('b2b.title', 'Tarifs B2B — Écoles')} loading={loading}>
      <div className="pricing-page-container">
        <Card className="pricing-header-card" bordered={false}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Typography.Title level={3} className="pricing-page-title">
              {t('b2b.title', 'Tarifs B2B — Écoles')}
            </Typography.Title>
            <p className="pricing-page-description">
              {t(
                'b2b.description',
                "Chaque école a un code que ses élèves saisissent pour devenir premium : c'est l'établissement qui est facturé, au prix négocié avec lui. Au-delà du seuil de sièges, TilmiSchool lui est offert."
              )}
            </p>
          </Space>
        </Card>

        {alertes.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('b2b.overflowTitle', '{{count}} école(s) dépassent leur effectif', {
              count: alertes.length,
            })}
            description={t(
              'b2b.overflowDesc',
              "Plus de sièges consommés que d'élèves inscrits : {{noms}}. Le code circule peut-être hors de l'établissement — ou son effectif n'est pas encore chargé, ce qui est fréquent en septembre. Aucun élève n'est bloqué.",
              { noms: alertes.map((e) => e.organization_login || `#${e.organization_id}`).join(', ') }
            )}
          />
        )}

        <Card
          className="pricing-form-card"
          title={t('b2b.new', 'Ajouter ou modifier un contrat école')}
          bordered={false}
          extra={
            <Button icon={<SyncOutlined />} onClick={onSync} loading={loading}>
              {t('b2b.sync', 'Recompter maintenant')}
            </Button>
          }
        >
          <p className="pricing-form-hint">
            {t(
              'b2b.formHint',
              "Le prix est celui de l'abonnement Tilmicode (Tilmicode + Tilmid+) par élève et par mois, négocié avec cet établissement. Le code est insensible à la casse : un parent tape ce qu'il lit sur un papier."
            )}
          </p>
          <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 900 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {/* Menu déroulant et non saisie d'un identifiant : se tromper d'ID
                  rattacherait le contrat — donc la facturation — au mauvais
                  établissement, sans que rien ne le signale. */}
              <Form.Item
                name="organization_id"
                label={t('b2b.org', "Établissement")}
                rules={[{ required: true, message: t('b2b.requiredOrg', "Choisissez l'établissement") }]}
                style={{ flex: '1 1 280px' }}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('b2b.orgPlaceholder', "Sélectionnez un établissement")}
                  options={organisations.map((o) => ({
                    value: o.id,
                    label: o.deja_sous_contrat
                      ? `${o.nom} (${t('b2b.alreadyContracted', 'déjà sous contrat')})`
                      : o.nom,
                  }))}
                  notFoundContent={t('b2b.noOrg', "Aucun compte établissement trouvé")}
                />
              </Form.Item>
              <Form.Item
                name="code"
                label={t('b2b.promoCode', 'Code promo')}
                rules={[{ required: true, message: t('b2b.requiredCode', 'Saisissez le code') }]}
                style={{ flex: '1 1 220px' }}
              >
                <Input placeholder="LYCEE-IBN-SINA-2026" />
              </Form.Item>
              <Form.Item
                name="prix_siege"
                label={t('b2b.seatPrice', 'Prix Tilmicode / élève / mois')}
                rules={[{ required: true, message: t('b2b.requiredPrice', 'Saisissez le prix') }]}
                style={{ flex: '0 0 150px' }}
              >
                <InputNumber min={0} step={5} style={{ width: '100%' }} placeholder="90" />
              </Form.Item>
              {/* Le second prix de l'école. Le contrat les porte tous les deux :
                  les saisir à deux endroits laissait deux valeurs coexister
                  sans qu'on sache laquelle faisait foi.
                  Laissé vide = cette école n'a pas TilmiSchool. */}
              <Form.Item
                name="prix_tilmischool"
                label={t('b2b.tilmischoolPrice', 'Prix TilmiSchool / élève / mois')}
                tooltip={t(
                  'b2b.tilmischoolPriceHint',
                  "Laissez vide si l'établissement n'a pas TilmiSchool. 0 = gratuit, mais facturé à montant nul."
                )}
                style={{ flex: '0 0 170px' }}
              >
                <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="3" />
              </Form.Item>
              <Form.Item
                name="devise"
                label={t('b2b.currency', 'Devise')}
                rules={[{ required: true, message: t('b2b.requiredCurrency', 'Choisissez une devise') }]}
                style={{ flex: '0 0 120px' }}
                initialValue="MAD"
              >
                <Select options={CURRENCY_OPTIONS} />
              </Form.Item>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <Form.Item
                name="periode"
                label={t('b2b.schoolYear', 'Année scolaire')}
                tooltip={t(
                  'b2b.schoolYearTooltip',
                  'Hors de cette période, le code ne peut plus ouvrir de siège. Laisser vide = aucune borne.'
                )}
                style={{ flex: '1 1 320px' }}
              >
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="seuil_gratuite"
                label={t('b2b.threshold', 'Seuil gratuité')}
                tooltip={t(
                  'b2b.thresholdTooltip',
                  "Nombre de sièges à partir duquel TilmiSchool devient gratuit pour cette école. Vide = aucune gratuité négociée."
                )}
                style={{ flex: '0 0 170px' }}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder={t('b2b.optional', 'Optionnel')} />
              </Form.Item>
              {/* QUI paie. Sans ce choix, un parent pouvait saisir le code d'une
                  ecole ayant prevu de regler elle-meme, et se retrouver facture
                  a sa place. */}
              <Form.Item
                name="mode_paiement"
                label={t('b2b.whoPays', 'Qui paie ?')}
                initialValue="parent"
                rules={[{ required: true }]}
                style={{ flex: '1 1 260px' }}
              >
                <Select
                  options={[
                    {
                      value: 'parent',
                      label: t('b2b.parentPays', 'Le parent paie (au tarif négocié)'),
                    },
                    {
                      value: 'ecole',
                      label: t('b2b.schoolPays', "L'établissement paie (parent : rien à régler)"),
                    },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="actif"
                label={t('b2b.activeLabel', 'Contrat actif')}
                valuePropName="checked"
                initialValue
                style={{ flex: '0 0 140px' }}
              >
                <Switch />
              </Form.Item>
            </div>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" className="pricing-save-btn" size="large">
                {t('b2b.save', 'Enregistrer le contrat')}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card className="pricing-table-card" title={t('b2b.list', 'Contrats écoles')} bordered={false}>
          <Table<TEcoleB2B>
            className="pricing-table"
            rowKey="promo_code_id"
            dataSource={ecoles}
            columns={columns}
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{
              emptyText: t('b2b.noSchools', 'Aucun contrat école. Ajoutez-en un avec le formulaire ci-dessus.'),
            }}
          />
        </Card>

        <Drawer
          width={560}
          open={!!selected}
          onClose={() => setSelected(null)}
          title={t('b2b.subscriptionsOf', 'Abonnements — {{ecole}}', {
            ecole: selected?.organization_login || `#${selected?.organization_id}`,
          })}
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message={t('b2b.quantityManaged', 'La quantité est tenue par le serveur')}
              description={t(
                'b2b.quantityManagedDesc',
                "Elle est recomptée automatiquement — sièges consommés, ou effectif pour TilmiSchool — et répercutée chez le prestataire. La saisir à la main désynchroniserait la base de la facture réellement émise."
              )}
            />

            <Table<TAbonnementOrg>
              size="small"
              rowKey="id"
              dataSource={abonnements}
              pagination={false}
              locale={{ emptyText: t('b2b.noSubscriptions', 'Aucun abonnement configuré') }}
              columns={[
                { title: t('b2b.offer', 'Offre'), dataIndex: 'offre_code', key: 'offre_code' },
                { title: t('b2b.vendor', 'Prestataire'), dataIndex: 'vendor', key: 'vendor' },
                {
                  title: t('b2b.unitPrice', 'Prix unitaire'),
                  key: 'prix_unitaire',
                  align: 'right',
                  render: (_, r) => money(r.prix_unitaire, r.devise),
                },
                { title: t('b2b.quantity', 'Qté'), dataIndex: 'quantite', key: 'quantite', align: 'right' },
                {
                  title: t('b2b.total', 'Total'),
                  key: 'total',
                  align: 'right',
                  render: (_, r) => <strong>{money(r.prix_unitaire * r.quantite, r.devise)}</strong>,
                },
              ]}
            />

            {signalements.length > 0 && (
              <Card size="small" title={t('b2b.reports', 'Signalements de l\'école')}>
                <p style={{ marginTop: 0, color: '#64748b', fontSize: 13 }}>
                  {t(
                    'b2b.reportsHint',
                    "L'école signale les élèves qu'elle ne reconnaît pas ; elle ne peut pas couper leur accès. Retirer un siège l'exclut du renouvellement : l'élève garde son premium jusqu'à la fin de la période payée, et le mois entamé reste facturé."
                  )}
                </p>
                <Table<TSignalement>
                  size="small"
                  rowKey="id"
                  dataSource={signalements}
                  pagination={false}
                  columns={[
                    { title: t('b2b.student', 'Élève'), dataIndex: 'student_id', key: 'student_id', width: 90 },
                    {
                      title: t('b2b.reason', 'Motif'),
                      dataIndex: 'motif',
                      key: 'motif',
                      render: (v: string) => v || <Typography.Text type="secondary">—</Typography.Text>,
                    },
                    {
                      title: t('b2b.status', 'Statut'),
                      key: 'statut',
                      width: 120,
                      render: (_, r) =>
                        r.statut === 'en_attente' ? (
                          <Tag color="orange">{t('b2b.sigPending', 'À traiter')}</Tag>
                        ) : r.statut === 'retire' ? (
                          <Tag color="red">{t('b2b.sigRemovedTag', 'Retiré')}</Tag>
                        ) : (
                          <Tag>{t('b2b.sigKeptTag', 'Confirmé')}</Tag>
                        ),
                    },
                    {
                      title: t('b2b.actions', 'Actions'),
                      key: 'actions',
                      width: 180,
                      render: (_, r) =>
                        r.statut !== 'en_attente' ? null : (
                          <Space size={2}>
                            <Popconfirm
                              title={t('b2b.confirmRemove', 'Retirer ce siège ?')}
                              description={t(
                                'b2b.confirmRemoveDesc',
                                "L'élève garde son accès jusqu'à la fin de la période payée. Le mois entamé reste facturé à l'école."
                              )}
                              okText={t('b2b.remove', 'Retirer')}
                              cancelText={t('b2b.cancel', 'Annuler')}
                              okButtonProps={{ danger: true }}
                              onConfirm={() => onTraiterSignalement(r, true)}
                            >
                              <Button type="link" size="small" danger>
                                {t('b2b.remove', 'Retirer')}
                              </Button>
                            </Popconfirm>
                            <Button type="link" size="small" onClick={() => onTraiterSignalement(r, false)}>
                              {t('b2b.keep', 'Conserver')}
                            </Button>
                          </Space>
                        ),
                    },
                  ]}
                />
              </Card>
            )}

            {/* Une ligne par offre, toujours affichees. « Ajouter un abonnement »
                dans un menu de deux entrees demandait deux clics pour un choix
                qui n'en est pas un — et faisait ressaisir le prix, la devise et
                des identifiants techniques que le serveur connait deja. */}
            <Card size="small" title={t('b2b.howPaid', 'Comment cette école règle-t-elle ?')}>
              <Space direction="vertical" size={14} style={{ width: '100%' }}>
                {OFFRES.map((offre) => {
                  const existant = abonnements.find((a) => a.offre_code === offre.code);
                  const gratuit =
                    offre.code === 'TILMISCHOOL' && selected?.tilmischool_gratuit === true;
                  return (
                    <LigneOffre
                      key={offre.code}
                      offre={offre}
                      existant={existant}
                      gratuit={gratuit}
                      seuil={selected?.seuil_gratuite}
                      siegesActifs={selected?.sieges_actifs ?? 0}
                      prixContrat={selected?.prix_siege ?? 0}
                      devise={selected?.devise || 'MAD'}
                      onSave={(values) => onSaveAbonnement(offre.code, values)}
                    />
                  );
                })}
              </Space>
            </Card>
          </Space>
        </Drawer>
      </div>
    </AppLayout>
  );
};

/**
 * Une offre et son mode de règlement.
 *
 * Deux questions seulement : l'école prend-elle cette offre, et par quel moyen
 * règle-t-elle ? Le prix des sièges et la devise viennent du contrat ; les
 * identifiants Lemon Squeezy sont résolus par le serveur.
 */
const LigneOffre: React.FC<{
  offre: (typeof OFFRES)[number];
  existant?: TAbonnementOrg;
  gratuit: boolean;
  seuil?: number;
  siegesActifs: number;
  prixContrat: number;
  devise: string;
  onSave: (values: AbonnementFormValues) => void;
}> = ({ offre, existant, gratuit, seuil, siegesActifs, prixContrat, devise, onSave }) => {
  const { t } = useTranslation();
  const [souscrit, setSouscrit] = useState(!!existant);
  const [vendor, setVendor] = useState(existant?.vendor || 'cashplus');
  const [prix, setPrix] = useState<number>(
    existant?.prix_unitaire ?? (offre.prixDuContrat ? prixContrat : offre.prixParDefaut ?? 0)
  );

  // Le tiroir se rouvre sur une autre école : repartir de SON abonnement, sinon
  // l'écran afficherait les réglages de la précédente.
  useEffect(() => {
    setSouscrit(!!existant);
    setVendor(existant?.vendor || 'cashplus');
    setPrix(existant?.prix_unitaire ?? (offre.prixDuContrat ? prixContrat : offre.prixParDefaut ?? 0));
  }, [existant, offre, prixContrat]);

  const prixAffiche = offre.prixDuContrat ? prixContrat : prix;

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Switch checked={souscrit} onChange={setSouscrit} size="small" />
        <Typography.Text strong style={{ flex: '1 1 160px' }}>
          {offre.label}
        </Typography.Text>
        <Typography.Text type="secondary">
          {money(prixAffiche, devise)} {t('b2b.perStudentMonth', '/ élève / mois')}
        </Typography.Text>
      </div>

      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
        {offre.aide}
      </Typography.Text>

      {gratuit && (
        <Alert
          type="success"
          showIcon
          style={{ marginTop: 8 }}
          message={t('b2b.freeNow', 'Gratuit actuellement')}
          description={t(
            'b2b.freeNowDesc',
            '{{sieges}} élèves premium ont atteint le seuil de {{seuil}}. La quantité facturée tombe à zéro, et remonte d’elle-même si l’école repasse sous le seuil.',
            { sieges: siegesActifs, seuil: seuil ?? 0 }
          )}
        />
      )}

      {/* Ni prix, ni prestataire a saisir ici.

          Le PRIX vient du contrat, au-dessus : c'est la seule source de verite.
          Il etait auparavant saisi ici aussi, si bien que deux valeurs
          pouvaient coexister pour la meme ecole sans qu'on sache laquelle
          faisait foi.

          Le PRESTATAIRE est choisi par l'ecole au moment de REGLER, sur son
          ecran de paiement — elle y choisit deja CashPlus, carte ou virement
          pour ses sieges. Le figer ici n'avait aucun effet sur ce choix, mais
          conditionnait le recomptage : sur « lemonsqueezy », il tentait
          d'ajuster la quantite chez un prestataire ou l'abonnement n'existait
          pas, echouait, et aucune echeance n'etait generee.

          Ce panneau ne sert donc plus qu'a SOUSCRIRE l'offre et a suivre ce
          qu'elle a produit. */}
      {souscrit && (
        <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 10 }}>
          <Button type="primary" size="small" onClick={() => onSave({ vendor, prix_unitaire: prix })}>
            {existant ? t('b2b.update', 'Mettre à jour') : t('b2b.saveSubscription', "Enregistrer l'abonnement")}
          </Button>
        </Space>
      )}
    </div>
  );
};

export default PricingB2B;
